import { useEffect, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { sort, uniq } from 'ramda'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from '@mui/material'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import axios, { pluckError } from 'src/utilities/http-client'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { RecertificationCycleTemplateCategory } from 'src/v3/core/types'

const DEFAULT_CATEGORIES = [
  'Airway/Respiratory/Ventilation',
  'Cardiovascular',
  'Critical Care',
  'Medical',
  'Operations',
  'Pediatrics',
  'Trauma',
]

type Props = {
  templateID: string
  category?: Partial<RecertificationCycleTemplateCategory>
  existingCategories: RecertificationCycleTemplateCategory[]
  open: boolean
  onClose: () => void
  onSaved: () => void
}

export default function TemplateCategoryForm({
  templateID,
  category,
  existingCategories,
  open,
  onClose,
  onSaved,
}: Props) {
  let { showError, NotificationComponent } = useNotifications()
  let [saving, setSaving] = useState(false)

  let groups = sort(
    (a, b) => a.localeCompare(b),
    uniq(existingCategories.map((c) => c.subcategory || '').filter(Boolean)),
  )

  let form = useForm({
    defaultValues: {
      id: category?.id,
      name: category?.name || '',
      hours: category?.hours?.toString() || '',
      subcategory: category?.subcategory || '',
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let request = !value.id ? axios.post : axios.put
      request<RecertificationCycleTemplateCategory>(
        '/api/manage/recertification-cycle/template/category',
        {
          id: value.id,
          templateID: !value.id ? templateID : undefined,
          name: value.name,
          hours: parseFloat(value.hours),
          subcategory: value.subcategory || undefined,
          categoryType: 'standard',
        },
      )
        .then(() => {
          onSaved()
          onClose()
          formApi.reset()
        })
        .catch((err) => {
          showError?.(pluckError(err, 'Error saving category'))
        })
        .finally(() => setSaving(false))
    },
  })

  useEffect(() => {
    if (category) {
      form.setFieldValue('id', category.id)
      form.setFieldValue('name', category.name || '')
      form.setFieldValue('hours', category.hours?.toString() || '')
      form.setFieldValue('subcategory', category.subcategory || '')
    }
  }, [category?.id])

  let handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <Paper sx={{ width: 'fit-content', px: 2, py: 1, minWidth: 320 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          {category?.id ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <form.Field
              name="subcategory"
              children={(field) => (
                <Autocomplete
                  freeSolo
                  value={field.state.value}
                  onChange={(_, val) => field.handleChange(val || '')}
                  onInputChange={(_, val) => field.handleChange(val)}
                  options={groups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Group"
                      sx={{ bgcolor: 'background.paper' }}
                      name={field.name}
                    />
                  )}
                />
              )}
            />
            <form.Field
              name="name"
              validators={{
                onSubmit: ({ value }) =>
                  !value ? 'Name is required' : undefined,
              }}
              children={(field) => (
                <Autocomplete
                  sx={{ my: 0.5 }}
                  freeSolo
                  value={field.state.value}
                  onChange={(_, val) => field.handleChange(val || '')}
                  onInputChange={(_, val) => field.handleChange(val)}
                  options={DEFAULT_CATEGORIES}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Name"
                      sx={{ bgcolor: 'background.paper' }}
                      name={field.name}
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                />
              )}
            />
            <form.Field
              name="hours"
              validators={{
                onSubmit: ({ value }) =>
                  !parseFloat(value)
                    ? 'Hours must be greater than 0'
                    : undefined,
              }}
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paper', mb: 0.5 }}
                  required
                  fullWidth
                  variant="outlined"
                  value={field.state.value}
                  label="Hours"
                  name={field.name}
                  onChange={(e) =>
                    field.handleChange(e.target.value.replace(/[^\d.]/g, ''))
                  }
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleClose}
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
